import { usePostHog } from "./usePostHog";

export function useAnalytics() {
  const { track, posthog } = usePostHog();

  const trackEvent = (eventName: string, properties?: Record<string, unknown>) => {
    if (typeof window !== "undefined" && track) {
      track(eventName, properties);
    }
  };

  const trackPageView = (page: string) => {
    if (typeof window !== "undefined" && track) {
      track("$pageview", {
        $current_url: window.location.href,
        page: page,
      });
    }
  };

  // **Nowe śledzenie - Chat Events**
  const trackConversationStarted = (moduleId: string) => {
    trackEvent("conversation_started", {
      module_id: moduleId,
      timestamp: new Date().toISOString(),
    });
  };

  const trackPromptSent = (
    moduleId: string,
    promptLength: number,
    hasImage?: boolean,
    hasFile?: boolean
  ) => {
    trackEvent("prompt_sent", {
      module_id: moduleId,
      prompt_length: promptLength,
      has_image: hasImage || false,
      has_file: hasFile || false,
      timestamp: new Date().toISOString(),
    });
  };

  const trackResponseReceived = (
    moduleId: string,
    responseLength: number,
    responseTime: number,
    tokensUsed?: number
  ) => {
    trackEvent("response_received", {
      module_id: moduleId,
      response_length: responseLength,
      response_time_ms: responseTime,
      tokens_used: tokensUsed,
      timestamp: new Date().toISOString(),
    });
  };

  // **User Journey Events**
  const trackOnboardingCompleted = (step: string) => {
    trackEvent("onboarding_completed", {
      completed_step: step,
      timestamp: new Date().toISOString(),
    });
  };

  const trackFirstSuccess = (moduleId: string, actionType: string) => {
    trackEvent("first_success", {
      module_id: moduleId,
      action_type: actionType,
      timestamp: new Date().toISOString(),
    });
  };

  const trackModuleUsage = (moduleId: string, timeSpent?: number) => {
    trackEvent("module_used", {
      module_id: moduleId,
      time_spent_seconds: timeSpent,
      timestamp: new Date().toISOString(),
    });
  };

  const trackUpgradeClick = (plan: string, location: string) => {
    trackEvent("upgrade_clicked", {
      target_plan: plan,
      click_location: location,
      timestamp: new Date().toISOString(),
    });
  };

  // **Performance Events**
  const trackPerformance = (
    action: string,
    duration: number,
    details?: Record<string, unknown>
  ) => {
    trackEvent("performance_metric", {
      action,
      duration_ms: duration,
      ...details,
      timestamp: new Date().toISOString(),
    });
  };

  // **Legacy functions - updated**
  const trackModuleSwitch = (fromModule: string, toModule: string) => {
    trackEvent("module_switch", {
      from_module: fromModule,
      to_module: toModule,
    });
  };

  const trackChatMessage = (moduleId: string, messageType: "user" | "ai") => {
    trackEvent("chat_message", {
      module_id: moduleId,
      message_type: messageType,
    });
  };

  const trackOfferSheetCreated = (offerSheetTitle: string) => {
    trackEvent("offer_sheet_created", {
      title: offerSheetTitle,
    });
  };

  const trackFileUpload = (fileName: string, fileType: string, fileSize: number) => {
    trackEvent("file_upload", {
      file_name: fileName,
      file_type: fileType,
      file_size_mb: Math.round((fileSize / 1024 / 1024) * 100) / 100,
    });
  };

  const trackWelcomeSurveyCompleted = () => {
    trackEvent("welcome_survey_completed");
  };

  const trackModuleUnlock = (moduleId: string, reason: string) => {
    trackEvent("module_unlock", {
      module_id: moduleId,
      unlock_reason: reason,
    });
  };

  const trackIdeaBankAction = (action: string, details?: Record<string, unknown>) => {
    trackEvent("idea_bank_action", {
      action,
      ...details,
    });
  };

  return {
    // Core tracking
    trackEvent,
    trackPageView,
    track,
    posthog,

    // New priority tracking
    trackConversationStarted,
    trackPromptSent,
    trackResponseReceived,
    trackOnboardingCompleted,
    trackFirstSuccess,
    trackModuleUsage,
    trackUpgradeClick,
    trackPerformance,

    // Legacy functions (still working)
    trackModuleSwitch,
    trackChatMessage,
    trackOfferSheetCreated,
    trackFileUpload,
    trackWelcomeSurveyCompleted,
    trackModuleUnlock,
    trackIdeaBankAction,
  };
}
